import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { fetchDecalBillById, trackBill, untrackBill } from "../lib/api";
import type { DecalBillDetail, BillHistoryEvent, DecalDomain } from "../lib/types";
import DomainPill from "../components/ui/DomainPill";
import SignalTypeBadge from "../components/ui/SignalTypeBadge";
import { cn } from "../lib/cn";

// ─── Risk color maps ──────────────────────────────────────────────────────────

const RISK_STRIP: Record<string, string> = {
  High:   "bg-risk-high",
  Medium: "bg-risk-medium",
  Low:    "bg-risk-low",
};
const RISK_TEXT: Record<string, string> = {
  High:   "text-risk-high",
  Medium: "text-risk-medium",
  Low:    "text-risk-low",
};
const RISK_BG: Record<string, string> = {
  High:   "bg-red-50",
  Medium: "bg-amber-50",
  Low:    "bg-teal-50",
};

// ─── Score display ────────────────────────────────────────────────────────────

const RISK_IMPACT_LABEL: Record<string, string> = {
  High:   "High Impact",
  Medium: "Moderate Impact",
  Low:    "Low Impact",
};
const URGENCY_SUBTEXT: Record<string, string> = {
  High:   "Immediate attention needed",
  Medium: "Monitor closely",
  Low:    "Track for awareness",
};

function ScoreDisplay({ score, risk, urgency }: { score: number; risk: string; urgency: string }) {
  return (
    <div className={cn(
      "flex w-28 shrink-0 flex-col items-center rounded-2xl px-4 py-5",
      RISK_BG[risk] ?? "bg-surface-sunken"
    )}>
      {/* Number with /100 context */}
      <div className="flex items-baseline gap-0.5">
        <span className={cn("text-4xl font-bold tabular-nums leading-none", RISK_TEXT[risk] ?? "text-ink")}
          aria-label={`Impact score ${score} out of 100`}>
          {score}
        </span>
        <span className="text-xs font-medium text-ink-tertiary">/100</span>
      </div>

      {/* Visual progress bar */}
      <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-black/10">
        <div
          className={cn("h-full rounded-full transition-all", RISK_STRIP[risk] ?? "bg-border")}
          style={{ width: `${score}%` }}
          aria-hidden
        />
      </div>

      {/* Impact label */}
      <p className={cn("mt-2 text-center text-2xs font-semibold leading-tight", RISK_TEXT[risk])}>
        {RISK_IMPACT_LABEL[risk] ?? `${risk} Risk`}
      </p>

      {/* Urgency context */}
      <p className="mt-1 text-center text-2xs leading-tight text-ink-tertiary">
        {URGENCY_SUBTEXT[urgency] ?? ""}
      </p>
    </div>
  );
}

// ─── Section heading ──────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-ink-tertiary">
      {children}
    </h2>
  );
}

// ─── Action flag row ──────────────────────────────────────────────────────────

function ActionFlag({ label, active }: { label: string; active: boolean }) {
  return (
    <div className={cn(
      "flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors",
      active ? "border-primary/20 bg-primary-light" : "border-border-subtle bg-surface"
    )}>
      <div className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
        active ? "bg-primary" : "bg-border"
      )} aria-hidden>
        {active ? (
          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="h-3 w-3 text-ink-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
          </svg>
        )}
      </div>
      <span className={cn("text-sm", active ? "font-semibold text-primary" : "font-medium text-ink-tertiary")}>
        {label}
      </span>
    </div>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

const CHAMBER_LABEL: Record<string, string> = { H: "House", S: "Senate", J: "Joint" };

function formatHistoryDate(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function Timeline({ events }: { events: BillHistoryEvent[] }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = [...events].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const KEY_LIMIT = 6;
  const hasMore = sorted.length > KEY_LIMIT;
  const visible = expanded ? sorted : sorted.slice(0, KEY_LIMIT);

  if (events.length === 0) {
    return <p className="text-sm text-ink-tertiary">No history available for this bill.</p>;
  }

  return (
    <div>
      <ol className="relative space-y-0">
        {visible.map((event, i) => {
          const isFirst = i === 0;
          const isLast = i === visible.length - 1;
          const chamberLabel = CHAMBER_LABEL[event.chamber] ?? event.chamber ?? null;
          return (
            <li key={`${event.date}-${i}`} className="relative flex gap-4 pb-5">
              {!isLast && (
                <div className="absolute left-[7px] top-3.5 bottom-0 w-px bg-border-subtle" aria-hidden />
              )}
              <div className={cn(
                "relative mt-1 flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border-2 bg-surface-raised",
                isFirst ? "border-primary" : "border-border"
              )} aria-hidden />
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={cn("shrink-0 font-mono text-xs tabular-nums",
                    isFirst ? "font-semibold text-ink-secondary" : "text-ink-tertiary")}>
                    {formatHistoryDate(event.date)}
                  </span>
                  {chamberLabel && (
                    <span className="shrink-0 rounded bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-ink-tertiary">
                      {chamberLabel}
                    </span>
                  )}
                </div>
                <p className={cn("mt-0.5 text-sm leading-snug",
                  isFirst ? "font-medium text-ink" : "text-ink-secondary")}>
                  {event.action}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {hasMore && (
        <button onClick={() => setExpanded((x) => !x)}
          className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:underline">
          {expanded ? "Show fewer events" : `Show ${sorted.length - KEY_LIMIT} earlier events`}
        </button>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6 animate-pulse">
      <div className="h-4 w-36 rounded bg-surface-sunken" />
      <div className="rounded-2xl border border-border-subtle bg-surface-raised overflow-hidden">
        <div className="h-1.5 w-full bg-surface-sunken" />
        <div className="p-7 flex items-start justify-between gap-6">
          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <div className="h-5 w-24 rounded-full bg-surface-sunken" />
            </div>
            <div className="h-3 w-44 rounded bg-surface-sunken" />
            <div className="space-y-2">
              <div className="h-6 w-full rounded bg-surface-sunken" />
              <div className="h-6 w-3/4 rounded bg-surface-sunken" />
            </div>
            <div className="h-3 w-64 rounded bg-surface-sunken" />
          </div>
          <div className="h-28 w-20 rounded-2xl bg-surface-sunken shrink-0" />
        </div>
      </div>
      {[1, 2, 3].map((n) => (
        <div key={n} className="rounded-2xl border border-border-subtle bg-surface-raised p-6 space-y-4">
          <div className="h-3 w-40 rounded bg-surface-sunken" />
          <div className="space-y-2">
            <div className="h-4 w-full rounded bg-surface-sunken" />
            <div className="h-4 w-5/6 rounded bg-surface-sunken" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Track button ─────────────────────────────────────────────────────────────

function TrackButton({ billId, initial }: { billId: number; initial: boolean }) {
  const [tracked, setTracked] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      if (tracked) {
        await untrackBill(billId);
        setTracked(false);
      } else {
        await trackBill(billId);
        setTracked(true);
      }
    } catch { /* silent — state stays as-is */ }
    setBusy(false);
  }

  return (
    <button
      onClick={toggle}
      disabled={busy}
      aria-label={tracked ? "Stop tracking this bill" : "Track this bill"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
        tracked
          ? "border-primary/30 bg-primary-light text-primary hover:bg-primary/10"
          : "border-border-subtle bg-surface text-ink-secondary hover:border-primary/30 hover:text-primary",
        busy && "opacity-50 cursor-not-allowed"
      )}
    >
      {tracked ? (
        <>
          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Tracking
        </>
      ) : (
        <>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Track this bill
        </>
      )}
    </button>
  );
}

// ─── Back link ────────────────────────────────────────────────────────────────

function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to}
      className="group mb-6 inline-flex items-center gap-1.5 text-sm text-ink-tertiary transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <svg className="h-4 w-4 transition-transform duration-150 group-hover:-translate-x-0.5"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      {label}
    </Link>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BillDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [bill, setBill] = useState<DecalBillDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const backTo = (location.state as { from?: string } | null)?.from ?? "/";
  const backLabel = backTo === "/" ? "Today's Signals" : "Back";

  useEffect(() => {
    if (!id) { setError("Missing bill ID"); setLoading(false); return; }
    const billId = parseInt(id, 10);
    if (isNaN(billId)) { setError("Invalid bill ID"); setLoading(false); return; }
    setLoading(true);
    setError(null);
    fetchDecalBillById(billId)
      .then(setBill)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load bill"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Skeleton />;

  if (error || !bill) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <BackLink to={backTo} label={backLabel} />
        <div className="rounded-2xl border border-risk-high-ring bg-risk-high-bg px-5 py-4">
          <p className="font-semibold text-risk-high">Unable to load bill</p>
          <p className="mt-1 text-sm text-risk-high/80">{error ?? "Bill not found"}</p>
        </div>
      </div>
    );
  }

  const hasAnyFlag = bill.requires_rule_changes || bill.requires_provider_outreach || bill.requires_data_system_changes;

  return (
    <div className="mx-auto max-w-3xl animate-fade-in px-6 py-8">

      <div className="mb-6 flex items-center justify-between">
        <BackLink to={backTo} label={backLabel} />
        <TrackButton billId={bill.bill_id} initial={bill.is_tracked} />
      </div>

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <section aria-label="Bill overview"
        className="mb-5 overflow-hidden rounded-2xl border border-border-subtle bg-surface-raised shadow-sm">
        <div className={cn("h-1.5 w-full", RISK_STRIP[bill.risk_level] ?? "bg-border")} aria-hidden />
        <div className="px-7 py-6">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              {bill.signals.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {[...new Set(bill.signals.map((s) => s.signal_type))].map((t) => (
                    <SignalTypeBadge key={t} type={t} size="sm" />
                  ))}
                </div>
              )}
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="rounded-md bg-surface-sunken px-2 py-0.5 font-mono text-xs font-medium text-ink-secondary">
                  {bill.state} · {bill.bill_number}
                </span>
                <span className="text-xs text-ink-tertiary">{bill.status_label}</span>
              </div>
              <h1 className="mb-3 text-xl font-semibold leading-snug text-ink">{bill.title}</h1>
              {bill.last_action && (
                <p className="mb-4 text-sm text-ink-secondary">
                  <span className="font-medium">Last action: </span>
                  <span className="text-ink-tertiary">{bill.last_action}</span>
                </p>
              )}
              {bill.decal_domains.filter((d) => d !== "OTHER_LOW_RELEVANCE").length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {bill.decal_domains.filter((d) => d !== "OTHER_LOW_RELEVANCE").map((d) => (
                    <DomainPill key={d} domain={d as DecalDomain} />
                  ))}
                </div>
              )}
            </div>
            <ScoreDisplay score={bill.impact_score} risk={bill.risk_level} urgency={bill.urgency} />
          </div>
        </div>
      </section>

      {/* ── Why it Matters for DECAL ──────────────────────────────────────── */}
      <section aria-label="Why it Matters for DECAL"
        className="mb-4 rounded-2xl border border-border-subtle bg-surface-raised p-6">
        <SectionHeading>Why it Matters for DECAL</SectionHeading>
        {bill.summary_for_commissioner ? (
          <p className="mb-5 text-sm leading-relaxed text-ink">{bill.summary_for_commissioner}</p>
        ) : (
          <p className="mb-5 text-sm text-ink-tertiary">No commissioner summary available.</p>
        )}
        {bill.program_areas_impacted.length > 0 && (
          <div>
            <p className="mb-2.5 text-xs font-medium text-ink-secondary">Program areas impacted</p>
            <div className="flex flex-wrap gap-1.5">
              {bill.program_areas_impacted.map((area, i) => (
                <span key={i}
                  className="inline-flex items-center rounded-full border border-border-subtle bg-surface px-3 py-1 text-xs font-medium text-ink-secondary">
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── What DECAL may need to do ──────────────────────────────────────── */}
      <section aria-label="What DECAL may need to do"
        className="mb-4 rounded-2xl border border-border-subtle bg-surface-raised p-6">
        <SectionHeading>What DECAL may need to do</SectionHeading>

        {bill.operational_impact.length > 0 ? (
          <ul className="mb-6 space-y-2.5" aria-label="Operational implications">
            {bill.operational_impact.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-ink">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-6 text-sm text-ink-tertiary">No specific operational implications identified.</p>
        )}

        <div className="mb-5 space-y-2" aria-label="Action flags">
          <p className="mb-2.5 text-xs font-medium text-ink-secondary">Action flags</p>
          <ActionFlag label="Requires rule or regulation changes" active={bill.requires_rule_changes} />
          <ActionFlag label="Requires provider outreach or communication" active={bill.requires_provider_outreach} />
          <ActionFlag label="Requires data or system changes" active={bill.requires_data_system_changes} />
        </div>

        {bill.estimated_staffing_impact && (
          <div className={cn("rounded-xl border px-4 py-4",
            hasAnyFlag ? "border-risk-medium-ring bg-risk-medium-bg" : "border-border-subtle bg-surface")}>
            <p className="mb-1 text-2xs font-semibold uppercase tracking-widest text-ink-tertiary">
              Estimated staffing impact
            </p>
            <p className={cn("text-sm font-medium leading-relaxed",
              hasAnyFlag ? "text-risk-medium" : "text-ink-secondary")}>
              {bill.estimated_staffing_impact}
            </p>
          </div>
        )}
      </section>

      {/* ── Score rationale ───────────────────────────────────────────────── */}
      {bill.explanation && (
        <section aria-label="Score rationale"
          className="mb-4 rounded-2xl border border-border-subtle bg-surface-raised p-6">
          <SectionHeading>Score rationale</SectionHeading>
          <p className="text-sm leading-relaxed text-ink-secondary">{bill.explanation}</p>
        </section>
      )}

      {/* ── Bill timeline ──────────────────────────────────────────────────── */}
      {bill.history.length > 0 && (
        <section aria-label="Bill timeline"
          className="rounded-2xl border border-border-subtle bg-surface-raised p-6">
          <SectionHeading>Bill timeline</SectionHeading>
          <Timeline events={bill.history} />
        </section>
      )}

    </div>
  );
}
